// @flow

import React from 'react';
import ReactDOM from 'react-dom';
import _ from 'lodash';
import { Provider } from 'react-redux';
import { StoreContext } from 'redux-react-hook';
import { enableBatching } from 'redux-batched-actions';
import { matchRoutes } from 'redux-router-kit';
import { $each, $eachPair, select } from 'qim';
import { applyMiddleware, compose, createStore } from 'redux';

import { ApolloProvider } from '@apollo/react-common';
import { DesignSystemProvider, ZinniaStylesReset } from '@zapier/design-system';
import { CurrentAccountIdProvider } from '@zapier/identity';
import { ExceptionTrackingProvider } from '@zapier/toolbox-error-tracking';
import { MegaphoneOverlay } from '@zapier/perf-utils';
import { trackException } from '@zapier/toolbox-redux-framework';
import {
  StateMachinesProvider,
  createStateMachineMiddleware,
  createStateMachineRegistry,
} from '@zapier/editor-core';
import {
  TrackingCookies,
  TrackingProvider,
  createEventTracker,
} from '@zapier/toolbox-tracking';

import * as LegacyUtils from 'app/legacy/LegacyUtils';
import AccountAndProfileProvider from 'app/common/components/contexts/AccountAndProfileProvider';
import DateProvider from 'app/common/components/DateProvider';
import LinkProvider from 'app/common/components/LinkProvider';
import Root from 'app/common/components/Root';
import RouterLinkWrapper from 'app/common/components/RouterLinkWrapper';
import StaticAssetProvider from 'app/common/components/StaticAssetProvider';
import WebUniversalNavProvider from 'app/common/components/contexts/WebUniversalNavProvider';
import createApolloClient from 'app/common/apollo/createApolloClient';
import createPerformanceLinterMiddleware from 'app/common/middlewares/performanceLinterMiddleware';
import createReducer from 'app/common/createReducer';
import getAllReducersAndMiddleware from 'app/common/getAllReducersAndMiddleware';

import { reset as resetPerformanceLinter } from 'app/perf-utils/PerformanceMonitoring';
import { trackTimeToMount } from 'app/common/middlewares/timeToMountTrackingMiddleware';
import RecordTimeToMountConnector from 'app/perf-utils/components/RecordTimeToMountConnector';

import type { Plugin, State } from 'app/common/types';

/*
  Build an app, given some plugins. Each plugin can have these properties:

    actionTypes:
      an object mapping action type keys to action type strings
    reducers:
      an object with any number of reducers, with a unique key across all reducers
    run:
      a function that accepts a redux store object and then does whatever it needs to do to get going
    routes:
      some redux-router-kit routes
*/

type CreateAppType = {
  initialReduxState: State,
  plugins: {
    routes: [],
    all: Array<Plugin>,
  },
};

const createApp = ({ initialReduxState, plugins }: CreateAppType) => {
  const routes = plugins.routes;
  const { reducers, rootReducer, ...middleware } = getAllReducersAndMiddleware(
    plugins
  );

  const stateMachineRegistry = createStateMachineRegistry();

  const {
    allMiddleware,
    authMiddleware,
    scrollToTopMiddleware,
    routerMiddleware,
  } = middleware;

  const createStoreWithMiddleware = compose(
    applyMiddleware(
      ...allMiddleware,
      createPerformanceLinterMiddleware(resetPerformanceLinter),
      createStateMachineMiddleware(stateMachineRegistry)
    ),
    window.__REDUX_DEVTOOLS_EXTENSION__
      ? window.__REDUX_DEVTOOLS_EXTENSION__({ trace: true, traceLimit: 50 })
      : f => f
  )(createStore);

  const store = createStoreWithMiddleware(rootReducer, initialReduxState);

  store.dispatch(
    trackTimeToMount({
      href: window.location.pathname,
      source: 'initial-page-load',
      timeToMount: performance.now(),
      timeToMountType: 'initial-page-load',
    })
  );

  const apolloClient = createApolloClient({ links: [] });
  const trackingFunctions = createEventTracker();

  const run = () => {
    plugins.all.forEach(plugin => {
      if (typeof plugin.run === 'function') {
        plugin.run({ history, store });
      }
    });

    // If a react root is present, and potentially contains server generated
    // markup, we want to keep it a little while the first React rendering
    // pass has a chance to happen. Changing it's id will prevent React from
    // flushing it right away and let the user see content during the first
    // render. Also some components (i.e. ZapbookStateful) need to access this
    // element to extract informations from it.
    // Eventually it will be moved into the app and stay there if we don't
    // need more than static rendering, if not it will be overriden by the
    // app itself.
    const initialContainerElement = document.getElementById('react-root-app');
    if (initialContainerElement) {
      initialContainerElement.id = 'react-root-app--server';
    }

    const containerElement = document.createElement('div');
    containerElement.id = 'react-root-app';

    const footerElement = document.getElementById('footer');
    if (document.body) {
      document.body.insertBefore(containerElement, footerElement);
    }

    // Checks to see if the header was sent along with the Django template
    // for the current page. See `app/viewport/components/HeaderContainer.js`
    // for usage of this.
    const serverHeader = document.querySelector('#header');
    const hasHeaderFromServer = Boolean(serverHeader);
    const serverHeaderType = serverHeader
      ? serverHeader.getAttribute('data-type')
      : undefined;

    // Store any UTM tracking information in a cookie, just in case we need
    // to send it up later, or append it to a URL for conversion tracking.
    TrackingCookies.storeUtms();

    // Store any Click ID tracking information in a cookie, for the same reasons
    // as storing the UTMs above.
    TrackingCookies.storeAllClickIds();
    const initialUrl = window.location.href.substring(
      window.location.origin.length
    );

    const initialMatch = matchRoutes(routes, initialUrl) || {};
    const initialRoutes = initialMatch.routes;
    const noop = () => {};
    const routeDocumentProps = _.get(
      initialRoutes,
      ['0', 'mapDocumentToProps'],
      noop
    );
    const initialRouteProps = routeDocumentProps(initialContainerElement);

    ReactDOM.render(
      <ExceptionTrackingProvider trackException={trackException}>
        <CurrentAccountIdProvider>
          <ApolloProvider client={apolloClient}>
            <Provider store={store}>
              <React.StrictMode>
                <StoreContext.Provider value={store}>
                  <RecordTimeToMountConnector>
                    <ZinniaStylesReset />
                    <DesignSystemProvider LinkComponent={RouterLinkWrapper}>
                      <LinkProvider>
                        <DateProvider>
                          <StaticAssetProvider>
                            <AccountAndProfileProvider>
                              <TrackingProvider
                                trackingFunctions={trackingFunctions}
                              >
                                <StateMachinesProvider
                                  value={stateMachineRegistry}
                                >
                                  <WebUniversalNavProvider>
                                    <Root
                                      hasHeaderFromServer={hasHeaderFromServer}
                                      initialContainerElement={
                                        initialContainerElement
                                      }
                                      initialRouteProps={initialRouteProps}
                                      routes={routes}
                                      serverHeaderType={serverHeaderType}
                                    />
                                    <MegaphoneOverlay />
                                  </WebUniversalNavProvider>
                                </StateMachinesProvider>
                              </TrackingProvider>
                            </AccountAndProfileProvider>
                          </StaticAssetProvider>
                        </DateProvider>
                      </LinkProvider>
                    </DesignSystemProvider>
                  </RecordTimeToMountConnector>
                </StoreContext.Provider>
              </React.StrictMode>
            </Provider>
          </ApolloProvider>
        </CurrentAccountIdProvider>
      </ExceptionTrackingProvider>,
      containerElement
    );

    // Dynamic routing needs to kick off a re-render, and we need to update
    // any other middleware that might have a handle on our routes.
    routerMiddleware.onRoutesChanged(newRoutes => {
      // Load in new reducers.
      const reducerPairs = select(
        [$each, 'app', 'reducers', $eachPair],
        newRoutes
      );
      reducerPairs.forEach(([reducerKey, routeReducer]) => {
        reducers[reducerKey] = routeReducer;
      });
      const newReducer = enableBatching(createReducer(reducers));
      store.replaceReducer(newReducer);
      authMiddleware.setRoutes(newRoutes);
      scrollToTopMiddleware.setRoutes(newRoutes);
      ReactDOM.render(
        <ExceptionTrackingProvider trackException={trackException}>
          <CurrentAccountIdProvider>
            <ApolloProvider client={apolloClient}>
              <Provider store={store}>
                <StoreContext.Provider value={store}>
                  <RecordTimeToMountConnector>
                    <ZinniaStylesReset />
                    <DesignSystemProvider LinkComponent={RouterLinkWrapper}>
                      <LinkProvider>
                        <DateProvider>
                          <StaticAssetProvider>
                            <AccountAndProfileProvider>
                              <TrackingProvider
                                trackingFunctions={trackingFunctions}
                              >
                                <StateMachinesProvider
                                  value={stateMachineRegistry}
                                >
                                  <WebUniversalNavProvider>
                                    <Root
                                      hasHeaderFromServer={hasHeaderFromServer}
                                      initialContainerElement={
                                        initialContainerElement
                                      }
                                      routes={newRoutes}
                                      serverHeaderType={serverHeaderType}
                                    />
                                    <MegaphoneOverlay />
                                  </WebUniversalNavProvider>
                                </StateMachinesProvider>
                              </TrackingProvider>
                            </AccountAndProfileProvider>
                          </StaticAssetProvider>
                        </DateProvider>
                      </LinkProvider>
                    </DesignSystemProvider>
                  </RecordTimeToMountConnector>
                </StoreContext.Provider>
              </Provider>
            </ApolloProvider>
          </CurrentAccountIdProvider>
        </ExceptionTrackingProvider>,
        containerElement
      );
    });

    LegacyUtils.bindLegacyEventHandlers();
  };

  return {
    history,
    store,
    run,
    apolloClient,
  };
};

export default createApp;
